import { motion } from 'framer-motion'
import { ArrowRight, Sparkles, Star } from 'lucide-react'
import React from 'react'

const techBadges = ['React', 'Node.js', 'Express', 'MongoDB', 'TypeScript', 'Tailwind']

export default function Hero() {
  return (
    <section id="home" className="relative overflow-hidden pt-32 pb-20 sm:pt-40 sm:pb-28">
      {/* Background layers */}
      <div className="absolute inset-0 grid-bg mask-fade-b opacity-60" />
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -left-24 h-96 w-96 rounded-full bg-brand-600/30 blur-[120px]" />
        <div className="absolute top-20 -right-24 h-96 w-96 rounded-full bg-accent-500/20 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-brand-400/10 blur-[100px]" />
      </div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-[1.1fr_0.9fr]">
          {/* Left: copy */}
          <div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-1.5 text-sm text-slate-300 backdrop-blur"
            >
              <Sparkles size={15} className="text-accent-400" />
              A web studio for modern businesses
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.08 }}
              className="mt-6 font-display text-4xl font-extrabold leading-[1.05] tracking-tight text-white sm:text-6xl lg:text-7xl"
            >
              We build <span className="text-gradient">dynamic</span> websites that grow your business
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.16 }}
              className="mt-6 max-w-xl text-lg leading-relaxed text-slate-400"
            >
              Raag is a web development studio crafting fast, beautiful, and fully responsive websites
              with the MERN stack — designed to turn visitors into customers on every screen.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.24 }}
              className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center"
            >
              <a
                href="#contact"
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand-500 px-7 py-3.5 text-base font-semibold text-ink-950 shadow-lg shadow-brand-500/30 transition-transform hover:scale-[1.03] active:scale-95"
              >
                Start your project
                <ArrowRight size={18} className="transition-transform group-hover:translate-x-1" />
              </a>
              <a
                href="#work"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-base font-semibold text-white backdrop-blur transition-colors hover:bg-white/10"
              >
                View our work
              </a>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.32 }}
              className="mt-8 flex items-center gap-4"
            >
              <div className="flex -space-x-2">
                {['AM', 'SO', 'LC', 'MR'].map((i) => (
                  <span
                    key={i}
                    className="grid h-9 w-9 place-items-center rounded-full border-2 border-ink-950 bg-gradient-to-br from-brand-400 to-brand-600 text-xs font-bold text-ink-950"
                  >
                    {i}
                  </span>
                ))}
              </div>
              <div className="text-sm text-slate-400">
                <div className="flex items-center gap-1 text-accent-400">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Star key={i} size={13} fill="currentColor" />
                  ))}
                </div>
                Trusted by 40+ businesses worldwide
              </div>
            </motion.div>
          </div>

          {/* Right: floating visual */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative hidden lg:block"
          >
            <HeroVisual />
          </motion.div>
        </div>

        {/* Tech marquee */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="mt-16 mask-fade-x overflow-hidden"
        >
          <div className="flex w-max gap-3" style={{ animation: 'marquee 30s linear infinite' }}>
            {[...techBadges, ...techBadges].map((t, i) => (
              <span
                key={i}
                className="whitespace-nowrap rounded-full border border-white/10 bg-white/5 px-5 py-2 text-sm font-medium text-slate-300"
              >
                {t}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  )
}

function HeroVisual() {
  return (
    <div className="relative h-[460px]">
      {/* Main browser card */}
      <motion.div
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
        className="absolute left-1/2 top-1/2 w-[420px] -translate-x-1/2 -translate-y-1/2 rounded-2xl border border-white/10 bg-ink-900/80 p-4 shadow-2xl backdrop-blur-xl card-glow"
      >
        <div className="mb-3 flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-full bg-red-400/70" />
          <span className="h-3 w-3 rounded-full bg-amber-400/70" />
          <span className="h-3 w-3 rounded-full bg-green-400/70" />
          <span className="ml-3 flex-1 rounded-md bg-white/5 px-2 py-1 text-xs text-slate-400">Raag Technologies</span>
        </div>
        <div className="space-y-3">
          <div className="h-24 rounded-xl bg-gradient-to-br from-brand-500/30 to-accent-500/20" />
          <div className="space-y-2">
            <div className="h-3 w-3/4 rounded bg-white/10" />
            <div className="h-3 w-1/2 rounded bg-white/10" />
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div className="h-16 rounded-lg bg-white/5" />
            <div className="h-16 rounded-lg bg-brand-500/20" />
            <div className="h-16 rounded-lg bg-white/5" />
          </div>
        </div>
      </motion.div>

      {/* Floating stat card */}
      <motion.div
        animate={{ y: [0, 14, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
        className="absolute -right-2 top-6 rounded-2xl border border-white/10 bg-ink-800/90 p-4 shadow-xl backdrop-blur-xl"
      >
        <div className="text-3xl font-bold text-gradient">+38%</div>
        <div className="mt-1 text-xs text-slate-400">avg. conversion lift</div>
      </motion.div>

      {/* Floating devices card */}
      <motion.div
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut', delay: 1 }}
        className="absolute -left-4 bottom-8 rounded-2xl border border-white/10 bg-ink-800/90 p-4 shadow-xl backdrop-blur-xl"
      >
        <div className="flex items-center gap-2 text-xs text-slate-300">
          <span className="h-2 w-2 rounded-full bg-green-400" />
          90+ Lighthouse
        </div>
        <div className="mt-2 flex gap-2">
          <div className="h-10 w-6 rounded border border-white/15 bg-white/5" />
          <div className="h-10 w-8 rounded border border-white/15 bg-white/5" />
          <div className="h-10 w-12 rounded border border-white/15 bg-brand-500/20" />
        </div>
      </motion.div>
    </div>
  )
}
